import * as React from 'react';
import { useRecordContext, useNotify } from 'react-admin';
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Button, Chip, Paper,
  TextField as MuiTextField, Typography,
} from '@material-ui/core';
import ExpandMore from '@material-ui/icons/ExpandMore';

/** The hosted tag, built from adserver-public/src/attribution/attribution.js. */
export const ATTRIBUTION_TAG_URL = 'https://public.servenobid.com/attribution/attribution.js';

/**
 * Global the bootstrap installs on the advertiser's page.
 *
 * Prefixed on purpose. An unprefixed word like `attribution` would collide both
 * ways: their variable makes our re-entry guard skip initialisation silently, and
 * ours could clobber theirs and break their site. Every vendor prefixes for this
 * reason - fbq, gtag, ttq. `nobid` was unavailable: the publisher-side tag already
 * owns window.nobid, along with nobidtag, nobidperf and nbTagRenderer.
 */
export const GLOBAL_NAME = 'nbAttribution';

/**
 * The only entry point the advertiser is asked to write.
 *
 * A dedicated function rather than nbAttribution('conversion', ...): a misspelled
 * action string is queued, dispatched to nothing and reports nothing, silently,
 * and gets found weeks later. A misspelled function name is a ReferenceError the
 * developer sees on first load, contained to that script block.
 */
export const CONVERSION_GLOBAL = 'nbConversion';

/**
 * Build the bootstrap that goes in the advertiser's <head>, on EVERY page.
 *
 * Only a loader. All behaviour lives in the hosted script, so we can fix bugs and
 * add features without every advertiser editing their site by hand - which is the
 * slowest step in deploying any of this.
 *
 * It installs the global as a QUEUE. A conversion call that runs before the hosted
 * script arrives is buffered rather than throwing a TypeError on the advertiser's
 * page, which is the one way this design could damage their site rather than just
 * lose us data. Same pattern as Meta's fbq stub.
 *
 * @param {number|string|undefined|null} code  The audience numeric code.
 * @returns {string} A <script> tag string, or '' when code is absent.
 */
export function buildBootstrapSnippet(code) {
  if (code === undefined || code === null || code === '') return '';
  return `<script>
!function(w,d,s,u){if(w.${GLOBAL_NAME})return;var n=w.${GLOBAL_NAME}=function(){` +
    `n.callMethod?n.callMethod.apply(n,arguments):n.q.push(arguments)};n.q=[];` +
    `w.${CONVERSION_GLOBAL}=function(o){n('conversion',o)};` +
    `var t=d.createElement(s);t.async=1;t.src=u;var f=d.getElementsByTagName(s)[0];` +
    `f.parentNode.insertBefore(t,f)}(window,document,'script','${ATTRIBUTION_TAG_URL}');
${GLOBAL_NAME}('init','${code}');
</script>`;
}

/**
 * The one line that marks a page as a conversion.
 *
 * This is the whole reason the tag can be safe site-wide: page load never reports a
 * conversion, so the only way to record one is for someone to write this. A script
 * block gets copy-pasted across a site; a bare call does not.
 */
export function buildConversionSnippet() {
  return `<script>${CONVERSION_GLOBAL}()</script>`;
}

/** The call without any order information, for pages that have none. Referenced
 *  inline in the instructions rather than offered as a second snippet to copy:
 *  two snippets side by side invite picking the simpler one, and a conversion
 *  with no value cannot contribute to ROAS. */
export function buildBareConversionCall() {
  return `${CONVERSION_GLOBAL}()`;
}

/**
 * The same call carrying order value, which is what makes ROAS reportable - without
 * it a $5 order and a $500 order count the same.
 *
 * Only value, currency and id are read; anything else is dropped by the tag rather
 * than forwarded, so a site handing us its whole checkout object cannot accidentally
 * send us PII. id is the dedup key for refreshed confirmation pages.
 */
export function buildConversionValueSnippet() {
  // The id placeholder is deliberately not a plausible value. 'ORDER-123' looks
  // like a working example, and anyone pasting it unchanged would dedupe every
  // order against the same key - the second order onward silently disappearing.
  return `<script>${CONVERSION_GLOBAL}({ value: 30.00, currency: 'USD', id: '<INSERT_ORDER_ID_HERE>' })</script>`;
}

/** Read-only block rendering a snippet with a copy-to-clipboard button. */
const PixelFormat = ({ label, snippet, rows, copyable = true }) => {
  const notify = useNotify();
  const copy = () => {
    navigator.clipboard.writeText(snippet).then(() => {
      notify(`${label} copied to clipboard`, { type: 'info' });
    }).catch(() => {
      notify('Failed to copy — please copy manually', { type: 'warning' });
    });
  };
  return (
    <Box style={{ marginBottom: 8 }}>
      {label && <Typography variant="caption" color="textSecondary">{label}</Typography>}
      <MuiTextField value={snippet} multiline fullWidth minRows={rows} InputProps={{ readOnly: true }} />
      {copyable && <Button onClick={copy} size="small" variant="outlined" style={{ marginTop: 8 }}>Copy</Button>}
    </Box>
  );
};

/** One numbered install step: what it is, where it goes, and the thing to copy. */
const Step = ({ number, title, placement, children }) => (
  <Paper variant="outlined" style={{ padding: 16, marginBottom: 16 }}>
    <Box style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
      <Typography variant="subtitle1" style={{ fontWeight: 600, marginRight: 12 }}>
        {number}. {title}
      </Typography>
      <Chip label={placement} size="small" color="primary" />
    </Box>
    {children}
  </Paper>
);

/**
 * Install instructions for the audience edit page.
 *
 * Deliberately two cards and two collapsed sections rather than prose. Adops needs
 * three facts to act - there are two steps, here is where each goes, click copy -
 * and everything else is background that competes with them for attention.
 */
const ConversionPixelField = () => {
  const record = useRecordContext();
  const code = record && record.code;
  const bootstrap = buildBootstrapSnippet(code);

  if (!bootstrap) {
    return (
      <Typography variant="body2">
        Save the audience first to generate its tag.
      </Typography>
    );
  }

  return (
    <Box style={{ gridColumn: '1 / -1' }}>
      <Step number={1} title="Site tag" placement="EVERY PAGE">
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Records which ad brought the visitor. Sends nothing. Place it in
          <code> &lt;head&gt;</code>, on every page including the conversion page.
        </Typography>
        <PixelFormat label="" snippet={bootstrap} rows={4} />
      </Step>

      <Step number={2} title="Conversion event" placement="CONVERSION PAGE ONLY">
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Reports the conversion. Add it only where an order or lead is confirmed —
          it counts a conversion every time it runs.
        </Typography>
        <PixelFormat label="" snippet={buildConversionValueSnippet()} rows={1} />
        <Typography variant="body2" color="textSecondary" gutterBottom>
          <strong>Replace <code>&lt;INSERT_ORDER_ID_HERE&gt;</code> with the order number
          from the page, and <code>30.00</code> with the order total.</strong> Both must be
          filled in by your site, not left as the example values.
        </Typography>
        <Typography variant="body2" color="textSecondary">
          <strong>No order information on the page?</strong> For a signup or a lead form,
          delete everything between the brackets and call
          <code> {buildBareConversionCall()}</code> on its own. The conversion is still
          recorded — it just carries no revenue.
        </Typography>
      </Step>

      <Accordion variant="outlined">
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="body2">Why two steps, and what to expect</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              The site tag is safe anywhere because it never reports anything — it only
              stores the click id locally. The conversion has to be an explicit call, so
              that installing the tag site-wide can never turn ordinary pageviews into
              conversions. Google and Meta split the job the same way.
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              <strong>Attribution window is 7 days.</strong> Safari shortens it to 24 hours
              when the visitor arrives on a link carrying a query parameter, so iOS
              conversions after the first day will under-report.
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              <strong>Conversions are still recorded without a click.</strong> Organic and
              unattributed conversions report normally; they simply carry no campaign.
            </Typography>
            <Typography variant="body2" color="textSecondary">
              <strong>Same site only.</strong> The click is remembered across subdomains
              (<code>www</code> → <code>checkout</code>), but not across different domains. If
              the conversion page is on another domain — a hosted checkout, for example —
              tell us, as that needs extra setup.
            </Typography>
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion variant="outlined">
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="body2">Tag source (for the advertiser&apos;s security review)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="textSecondary">
            The snippet above loads <a href={ATTRIBUTION_TAG_URL} target="_blank" rel="noopener noreferrer">{ATTRIBUTION_TAG_URL}</a>,
            which reads the <code>nbclid</code> parameter into a first-party cookie and posts a
            conversion when called. It sets no third-party cookies, reads no user
            identifiers, and strips the query string and hash from the page URL before
            reporting so order ids and email addresses are never sent.
          </Typography>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default ConversionPixelField;
